import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { ViewPaths, ViewConfig } from '@/types/views';
import { useCallback, useMemo } from 'react';
import { Loading } from '@/components/Loading';
import Header from '@/components/Header/Header';
import { HeaderMenu } from '@/components/Header/HeaderMenu';
import {
  getViewsForType,
  isValidView,
  getDefaultView,
} from '@/utils/viewHelpers';
import { DataNotFound } from '@/components/DataNotFound';
import { AdministratorProfileForm } from '@/components/Administrators/AdministratorProfileForm';
import { AdministratorAdvancedForm } from '@/components/Administrators/AdministratorAdvancedForm';
import { CenteredColumn } from '@/components/CenteredColumn';
import { FormattedMessage } from 'react-intl';
import { useGetAllAdmins } from '@/hooks/useGetAllAdmins';
import { DocumentHead } from '@/components/core/DocumentHead';
import { useAuth } from '@/stores/auth';
import { CurrentUserBadge } from '@/components/CurrentUserBadge';

export const Route = createFileRoute(
  '/$tenantId/_layout/administrators/$administratorId/$view',
)({
  component: AdministratorDetailView,
  beforeLoad: ({ params: { tenantId, administratorId, view } }) => {
    if (!isValidView(view, 'administrator')) {
      throw redirect({
        to: '/$tenantId/administrators/$administratorId/$view',
        params: {
          tenantId,
          administratorId,
          view: getDefaultView('administrator'),
        },
      });
    }
  },
});

function AdministratorDetailView() {
  const { tenantId, administratorId, view } = Route.useParams();
  const selected = view as ViewPaths<'administrator'>;
  const navigate = useNavigate();
  const { userProfile } = useAuth();

  const { admins, isLoading } = useGetAllAdmins();

  const administrator = admins?.find((admin) => admin.id === administratorId);

  const isCurrentUser =
    userProfile?.email && administrator?.email === userProfile.email;

  const onViewChange = useCallback(
    (view?: string) => {
      if (!view) return;

      navigate({
        to: Route.to,
        params: { tenantId, administratorId, view },
        replace: false,
      });
    },
    [navigate, tenantId, administratorId],
  );

  const views = useMemo(() => {
    return getViewsForType('administrator') as ViewConfig[];
  }, []);

  const metadataKeys = useMemo(() => {
    const administratorName = administrator
      ? `${administrator.firstname} ${administrator.lastname}`
      : 'Administratör';

    switch (selected) {
      case 'profile':
        return {
          title: `${administratorName} - Profil`,
          description: `Profilinformation för administratören ${administratorName}`,
        };
      case 'advanced':
        return {
          title: `${administratorName} - Avancerat`,
          description: `Avancerade inställningar för administratören ${administratorName}`,
        };
      default:
        return {
          title: `${administratorName}`,
          description: `Detaljer för administratören ${administratorName}`,
        };
    }
  }, [selected, administrator]);

  if (isLoading) {
    return <Loading />;
  }

  if (!administrator) {
    return (
      <DataNotFound
        description={
          <FormattedMessage defaultMessage="Administratören finns inte eller så har du inte rättighet att se detailjer om hen." />
        }
        buttonTitle={<FormattedMessage defaultMessage="Till administratörer" />}
        onClick={() =>
          navigate({ to: '/$tenantId/administrators', params: { tenantId } })
        }
      />
    );
  }

  const { firstname, lastname } = administrator;

  return (
    <div className="overflow-y-auto">
      <DocumentHead
        title={metadataKeys.title}
        description={metadataKeys.description}
      />
      <Header
        title={
          <span className="flex items-center gap-2">
            <span>
              {firstname} {lastname}
            </span>
            {isCurrentUser && <CurrentUserBadge />}
          </span>
        }
        menu={
          <HeaderMenu
            options={views}
            selected={selected}
            onViewChange={onViewChange}
          />
        }
      />
      <CenteredColumn>
        {selected === 'profile' && (
          <AdministratorProfileForm administrator={administrator} />
        )}
        {selected === 'advanced' && (
          <AdministratorAdvancedForm administrator={administrator} />
        )}
      </CenteredColumn>
    </div>
  );
}
